<div
  class="home-studio h-screen flex flex-col overflow-hidden font-sans bg-[#f7f4ff]"
>
  <!-- ═══ HEADER ═══════════════════════════════════════════════════════════ -->
  <header
    class="h-[72px] flex items-center justify-between px-6 bg-white/95 border-b border-green-100 z-10 shrink-0 shadow-[0_1px_0_rgba(72,36,126,0.04)] backdrop-blur"
  >
    <div class="flex items-center gap-4 min-w-0">
      <div
        class="w-10 h-10 rounded-[14px] bg-[#3d0a73] flex items-center justify-center text-white shadow-lg shadow-green-900/20"
      >
        <i class="fi fi-rr-apps-add"></i>
      </div>
      <div class="min-w-0">
        <h1
          class="font-black text-slate-950 tracking-tight text-lg leading-tight"
        >
          Home Layout Studio
        </h1>
        <p
          class="text-[10px] font-bold text-green-500 uppercase tracking-[0.16em]"
        >
          Supidoo style live CMS
        </p>
      </div>
    </div>

    <div
      class="hidden xl:flex items-center gap-2 rounded-2xl bg-green-50/80 border border-green-100 p-1"
    >
      @for (stat of studioStats; track stat.label) {
      <div class="min-w-20 px-3 py-2">
        <p
          class="text-[9px] font-black uppercase tracking-widest text-green-400"
        >
          {{ stat.label }}
        </p>
        <p class="text-sm font-black text-green-950 leading-tight">
          {{ stat.value() }}
        </p>
      </div>
      }
    </div>

    <div class="flex items-center gap-3 shrink-0">
      <button
        (click)="backfillActiveImages()"
        [disabled]="isBackfillingImages || isSaving"
        class="btn btn-sm btn-ghost border border-green-200 rounded-xl font-black uppercase text-[10px] tracking-[0.08em]"
      >
        @if (isBackfillingImages) {
        <span class="loading loading-spinner loading-xs"></span>
        Optimizing... } @else {
        Optimize active images }
      </button>
      <!-- Dirty / Saved Badge -->
      @if (isDirty) {
      <div
        class="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg animate-pulse"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
        Unpublished changes
      </div>
      } @else if (!isLoading) {
      <div
        class="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg"
      >
        <i class="fi fi-rs-check-circle text-sm"></i>
        Published
      </div>
      }

      <button
        (click)="saveLayout()"
        [disabled]="isSaving || !isDirty"
        class="btn btn-sm border-0 bg-[#3d0a73] hover:bg-[#2b0752] text-white px-6 rounded-xl font-black uppercase text-[10px] tracking-[0.1em] shadow-lg shadow-green-900/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
      >
        @if (isSaving) {
        <span class="loading loading-spinner text-white loading-xs"></span>
        Publishing... } @else {
        <i class="fi fi-rs-rocket-launch mr-1.5"></i>
        Publish to App }
      </button>
    </div>
  </header>

  <!-- ═══ MAIN 3-PANEL LAYOUT ══════════════════════════════════════════════ -->
  <main class="flex-1 flex overflow-hidden">
    <!-- ─── LEFT PANEL: Tabs + Sections ─────────────────────────────────── -->
    <aside
      class="w-[308px] bg-white/90 backdrop-blur-xl border-r border-green-100 flex flex-col shrink-0 overflow-hidden"
    >
      <div class="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar">
        <!-- Section: Navigation Tabs -->
        <div class="flex items-center justify-between mb-4">
          <h2
            class="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400"
          >
            Navigation Tabs
          </h2>
          <button
            (click)="openTabModal()"
            class="btn btn-ghost btn-xs text-primary bg-primary/5 hover:bg-primary/10 font-black rounded-lg gap-1"
          >
            <i class="fi fi-rs-plus-small text-base leading-none"></i>ADD
          </button>
        </div>

        <!-- Empty State: 0 tabs -->
        @if (!isLoading && tabs.length === 0) {
        <div
          class="flex flex-col items-center py-12 text-center gap-3 opacity-50"
        >
          <div
            class="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center"
          >
            <i class="fi fi-rs-apps text-2xl text-slate-300"></i>
          </div>
          <p
            class="text-[10px] font-black uppercase tracking-widest text-slate-400"
          >
            No tabs yet
          </p>
          <button
            (click)="openTabModal()"
            class="btn btn-primary btn-xs rounded-xl font-black text-[10px] px-4"
          >
            Create First Tab
          </button>
        </div>
        }

        <!-- Tab List (draggable) -->
        <div
          cdkDropList
          (cdkDropListDropped)="onTabsReordered($event)"
          class="space-y-2"
        >
          @for (tab of tabs; track tab.id) {
          <div
            cdkDrag
            (click)="selectTab(tab.id)"
            (keyup.enter)="selectTab(tab.id)"
            tabindex="0"
            role="button"
            [attr.aria-pressed]="activeTabId === tab.id"
            class="group flex items-center gap-3 p-3 rounded-2xl cursor-pointer transition-all border outline-none"
            [ngClass]="
              activeTabId === tab.id
                ? 'bg-white border-primary/20 shadow-[0_4px_20px_rgba(0,0,0,0.06)] ring-1 ring-primary/10'
                : 'border-slate-100/50 bg-white/40 hover:bg-white hover:border-slate-200 hover:shadow-sm'
            "
          >
            <div
              class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors p-1.5 overflow-hidden"
              [ngClass]="
                tab.is_active
                  ? 'bg-primary/10 text-primary'
                  : 'bg-slate-100 text-slate-400'
              "
            >
              @if (tab.icon_url) {
              <img
                [src]="tab.icon_url"
                class="w-full h-full object-contain"
                [alt]="tab.name"
              />
              } @else {
              <i class="fi fi-rs-star text-xs opacity-20"></i>
              }
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold truncate text-slate-800">
                {{ tab.name }}
              </p>
              <div class="flex items-center gap-1.5 mt-0.5">
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  [ngClass]="tab.is_active ? 'bg-emerald-400' : 'bg-slate-300'"
                ></span>
                <p
                  class="text-[9px] font-bold text-slate-400 uppercase tracking-widest"
                >
                  {{ tab.is_active ? 'Visible' : 'Hidden' }}
                </p>
                <span class="text-[9px] text-slate-300 font-bold">·</span>
                <p class="text-[9px] font-bold text-slate-400">
                  {{ tab.sections.length }} sections
                </p>
              </div>
            </div>

            <!-- Delete inline confirmation -->
            @if (pendingDeleteTabId === tab.id) {
            <div
              class="flex items-center gap-1 bg-red-50 border border-red-200 rounded-xl p-1"
              role="none"
              (click)="$event.stopPropagation()"
            >
              <span class="text-[9px] font-black text-red-500 px-1"
                >Delete?</span
              >
              <button
                class="btn btn-error btn-xs btn-square rounded-lg text-white"
                title="Confirm delete"
                (click)="confirmDeleteTab(tab.id, $event)"
              >
                <i class="fi fi-rs-check text-[10px]"></i>
              </button>
              <button
                class="btn btn-ghost btn-xs btn-square rounded-lg text-slate-400 hover:text-slate-600"
                title="Cancel"
                (click)="cancelDeleteTab($event)"
              >
                <i class="fi fi-rs-cross-small text-sm"></i>
              </button>
            </div>
            } @else {
            <!-- Normal hover actions -->
            <div
              class="opacity-0 group-hover:opacity-100 transition-opacity flex items-center bg-white rounded-lg shadow-sm border border-slate-100 p-0.5"
              role="none"
              (click)="$event.stopPropagation()"
              (keydown)="$event.stopPropagation()"
            >
              <button
                class="btn btn-ghost btn-xs btn-square text-slate-400 hover:text-primary hover:bg-primary/10 rounded-md"
                title="Edit tab"
                (click)="openTabModal(tab)"
              >
                <i class="fi fi-rs-pencil"></i>
              </button>
              <button
                class="btn btn-ghost btn-xs btn-square rounded-md"
                [ngClass]="
                  tab.is_active
                    ? 'text-emerald-500 hover:bg-emerald-50'
                    : 'text-slate-400 hover:bg-slate-50'
                "
                title="Toggle visibility"
                (click)="toggleTabActive(tab, $event)"
              >
                <i
                  class="fi"
                  [ngClass]="tab.is_active ? 'fi-rs-eye' : 'fi-rs-eye-crossed'"
                ></i>
              </button>
              <button
                class="btn btn-ghost btn-xs btn-square text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                title="Delete tab"
                (click)="requestDeleteTab(tab.id, $event)"
              >
                <i class="fi fi-rs-trash"></i>
              </button>
              <div
                cdkDragHandle
                class="btn btn-ghost btn-xs btn-square text-slate-300 hover:text-slate-600 cursor-grab rounded-md"
              >
                <i class="fi fi-rs-grip-vertical"></i>
              </div>
            </div>
            }
          </div>
          }
        </div>

        <!-- Sections of active tab -->
        @if (activeTab) {
        <div class="mt-6 pt-5 border-t border-slate-100">
          <div class="flex items-center justify-between mb-3">
            <h3
              class="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400"
            >
              Sections
              <span
                class="ml-1 px-1.5 py-0.5 bg-slate-100 rounded text-slate-500"
                >{{ activeTab.sections.length }}</span
              >
            </h3>
            <button
              (click)="toggleAddSection()"
              class="btn btn-ghost btn-xs font-black rounded-lg gap-1"
              [ngClass]="
                showAddSection
                  ? 'text-primary bg-primary/10'
                  : 'text-primary bg-primary/5 hover:bg-primary/10'
              "
            >
              <i
                class="fi text-base leading-none"
                [ngClass]="
                  showAddSection ? 'fi-rs-cross-small' : 'fi-rs-plus-small'
                "
              ></i>
              {{ showAddSection ? 'CANCEL' : 'ADD' }}
            </button>
          </div>

          <!-- Section Type Picker -->
          @if (showAddSection) {
          <div
            class="mb-3 p-3 bg-primary/5 border border-primary/15 rounded-2xl space-y-1.5"
          >
            <p
              class="text-[9px] font-black uppercase tracking-widest text-primary/60 mb-2 px-1"
            >
              Choose a section type
            </p>
            @for (st of sectionTypes; track st.type) {
            <button
              (click)="addSection(st.type)"
              class="w-full flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm transition-all text-left group/sec"
            >
              <div
                class="w-16 h-11 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-primary group-hover/sec:border-primary/20 transition-colors shrink-0 overflow-hidden"
              >
                @switch (st.type) {
                  @case ('banner') { <div class="w-12 h-7 rounded-md bg-primary/20 border border-primary/20 flex items-end p-1"><span class="w-5 h-1 rounded bg-primary/60"></span></div> }
                  @case ('category_grid') { <div class="grid grid-cols-4 gap-1">@for (_ of [1,2,3,4,5,6,7,8]; track $index) { <span class="w-2 h-2 rounded-sm bg-primary/30"></span> }</div> }
                  @case ('brand_grid') { <div class="flex gap-1">@for (_ of [1,2,3]; track $index) { <span class="w-3.5 h-3.5 rounded-full bg-primary/25 border border-primary/20"></span> }</div> }
                  @case ('collection_banner') { <div class="w-12 h-8 grid grid-cols-3 gap-1"><span class="col-span-2 row-span-2 rounded bg-primary/30"></span><span class="rounded bg-primary/15"></span><span class="rounded bg-primary/15"></span></div> }
                  @case ('featured_cards') { <div class="flex gap-1"><span class="w-7 h-8 rounded bg-primary/25"></span><span class="w-7 h-8 rounded bg-primary/15"></span></div> }
                  @case ('promo_carousel') { <div class="w-12 h-7 rounded bg-gradient-to-r from-primary/30 to-amber-200 border border-primary/10"></div> }
                  @case ('product_shelf') { <div class="flex gap-1">@for (_ of [1,2,3]; track $index) { <span class="w-3.5 h-7 rounded bg-white border border-primary/20"><span class="block h-4 bg-primary/15 rounded-t"></span></span> }</div> }
                }
              </div>
              <div class="min-w-0">
                <p class="text-xs font-black text-slate-800">{{ st.label }}</p>
                <p class="text-[9px] text-slate-400 font-bold">
                  {{ st.description }}
                </p>
              </div>
            </button>
            }
          </div>
          }

          <!-- Section list (draggable) -->
          <div
            cdkDropList
            (cdkDropListDropped)="onSectionsReordered($event)"
            class="space-y-1.5"
          >
            @for (section of activeTab.sections; track section.id) {
            <div
              cdkDrag
              (click)="selectSection(section.id)"
              (keyup.enter)="selectSection(section.id)"
              tabindex="0"
              role="button"
              [attr.aria-pressed]="selectedSectionId === section.id"
              class="group/sec flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all border"
              [ngClass]="
                selectedSectionId === section.id
                  ? 'bg-primary/10 border-primary/25 text-primary'
                  : 'bg-white/60 border-slate-100 hover:bg-white hover:border-slate-200 text-slate-600'
              "
            >
              <div
                cdkDragHandle
                class="text-slate-300 hover:text-slate-500 cursor-grab shrink-0"
              >
                <i class="fi fi-rs-grip-dots-vertical text-xs"></i>
              </div>

              <div
                class="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                [ngClass]="
                  selectedSectionId === section.id
                    ? 'bg-primary/20 text-primary'
                    : 'bg-slate-100 text-slate-400'
                "
              >
                <i
                  class="fi {{ getSectionIcon(section.type) }} text-[10px]"
                ></i>
              </div>

              <span
                class="flex-1 text-xs font-bold truncate"
                [ngClass]="!section.is_active ? 'line-through opacity-40' : ''"
              >
                {{ section.name || section.type }}
              </span>

              <!-- Inline section actions -->
              @if (pendingDeleteSectionId === section.id) {
              <div
                class="flex items-center gap-1"
                role="none"
                (click)="$event.stopPropagation()"
              >
                <button
                  class="btn btn-error btn-xs btn-square rounded-lg text-white"
                  (click)="confirmDeleteSection(section.id, $event)"
                >
                  <i class="fi fi-rs-check text-[10px]"></i>
                </button>
                <button
                  class="btn btn-ghost btn-xs btn-square rounded-lg text-slate-400"
                  (click)="cancelDeleteSection($event)"
                >
                  <i class="fi fi-rs-cross-small text-xs"></i>
                </button>
              </div>
              } @else {
              <div
                class="flex items-center gap-1 opacity-0 group-hover/sec:opacity-100 transition-opacity"
                role="none"
                (click)="$event.stopPropagation()"
              >
                <!-- Toggle visibility -->
                <button
                  class="w-6 h-6 rounded-lg flex items-center justify-center transition-colors"
                  [ngClass]="
                    section.is_active
                      ? 'text-emerald-500 hover:bg-emerald-50'
                      : 'text-slate-300 hover:bg-slate-100'
                  "
                  title="Toggle section"
                  (click)="toggleSectionActive(section, $event)"
                >
                  <i
                    class="fi text-[10px]"
                    [ngClass]="
                      section.is_active ? 'fi-rs-eye' : 'fi-rs-eye-crossed'
                    "
                  ></i>
                </button>
                <!-- Delete -->
                <button
                  class="w-6 h-6 rounded-lg flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Delete section"
                  (click)="requestDeleteSection(section.id, $event)"
                >
                  <i class="fi fi-rs-trash text-[10px]"></i>
                </button>
              </div>
              }
            </div>
            } @if (activeTab.sections.length === 0 && !showAddSection) {
            <div
              class="flex flex-col items-center justify-center py-8 text-center opacity-40"
            >
              <i class="fi fi-rs-layers text-2xl text-slate-300 mb-2"></i>
              <p
                class="text-[10px] font-black uppercase tracking-widest text-slate-400"
              >
                No sections yet
              </p>
              <p class="text-[9px] text-slate-400 mt-1">
                Click ADD above to get started
              </p>
            </div>
            }
          </div>
        </div>
        }
      </div>
    </aside>

    <!-- ─── CENTER PANEL: Mock Preview ───────────────────────────────────── -->
    <section
      class="flex-1 min-w-0 bg-[#f7f4ff] overflow-hidden flex items-center justify-center p-4 relative z-0"
    >
      <div
        class="absolute inset-0 bg-[linear-gradient(to_right,rgba(76,29,149,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(76,29,149,0.06)_1px,transparent_1px)] bg-[size:28px_28px]"
      ></div>
      <div class="relative flex items-center justify-center">
        <app-mock-preview
          [sections]="activeTab?.sections || []"
          [tabs]="tabs"
          [activeTabId]="activeTabId"
          [themeName]="activeTab?.theme_name || 'default'"
          [themeId]="activeTab?.theme_id || 'default'"
          [availableCategories]="availableCategories"
          (sectionSelected)="selectSection($event)"
        >
        </app-mock-preview>
      </div>
    </section>

    <!-- ─── RIGHT PANEL: Inspector ───────────────────────────────────────── -->
    <aside
      class="w-[400px] bg-white border-l border-green-100 z-10 flex flex-col shrink-0 shadow-[-10px_0_30px_rgba(76,29,149,0.04)]"
    >
      @if (selectedSection) {
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Inspector Header -->
        <div class="p-5 border-b border-slate-100 bg-slate-50/50 shrink-0">
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center gap-3">
              <div
                class="w-11 h-11 rounded-2xl bg-white text-primary flex items-center justify-center shadow-sm border border-slate-100"
              >
                <i class="fi fi-rs-settings text-lg"></i>
              </div>
              <div>
                <h3 class="font-black text-base text-slate-800">
                  Section Params
                </h3>
                <div class="flex items-center mt-0.5 gap-1.5">
                  <span
                    class="w-1.5 h-1.5 rounded-full animate-pulse"
                    [ngClass]="
                      selectedSection.is_active
                        ? 'bg-emerald-400'
                        : 'bg-slate-300'
                    "
                  ></span>
                  <span
                    class="font-bold text-[10px] text-slate-400 uppercase tracking-widest"
                  >
                    {{ selectedSection.type.replace('_', ' ') }}
                  </span>
                  @if (!selectedSection.is_active) {
                  <span
                    class="text-[9px] font-black text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200"
                    >Hidden</span
                  >
                  }
                </div>
              </div>
            </div>
            <button
              (click)="requestDeleteSection(selectedSection.id)"
              class="btn btn-ghost btn-sm btn-square text-red-400 hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-colors rounded-xl"
            >
              <i class="fi fi-rs-trash"></i>
            </button>
          </div>

          <!-- Section name + active toggle -->
          <div class="flex items-center gap-2">
            <input
              type="text"
              [(ngModel)]="selectedSection.name"
              (ngModelChange)="markDirty()"
              class="input input-sm flex-1 bg-white rounded-xl font-bold border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 shadow-sm transition-all text-slate-800"
              placeholder="Section label (internal)"
            />
            <label
              class="cursor-pointer flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 hover:bg-slate-100 transition-colors"
              title="Toggle visibility"
            >
              <span
                class="text-[10px] font-black text-slate-500 uppercase tracking-wider"
                >Visible</span
              >
              <input
                type="checkbox"
                [(ngModel)]="selectedSection.is_active"
                (ngModelChange)="markDirty()"
                class="toggle toggle-primary toggle-sm shadow-sm"
              />
            </label>
          </div>
        </div>

        <!-- Inspector Config -->
        <div class="flex-1 overflow-y-auto p-5 bg-white no-scrollbar">
          <app-section-config
            [section]="selectedSection"
            [availableCategories]="availableCategories"
            (configChanged)="markDirty()"
          >
          </app-section-config>
        </div>

        <!-- Save reminder -->
        @if (isDirty) {
        <div class="p-4 bg-amber-50 border-t border-amber-100 shrink-0">
          <div class="flex items-center gap-2">
            <i class="fi fi-rs-exclamation text-amber-500 text-sm shrink-0"></i>
            <p class="text-[10px] font-bold text-amber-700 leading-tight">
              Changes are staged locally. Hit <strong>Publish to App</strong> to
              save permanently.
            </p>
          </div>
        </div>
        }
      </div>

      } @else {
      <div
        class="flex-1 flex flex-col items-center justify-center p-12 text-center"
      >
        <div
          class="w-20 h-20 mb-5 rounded-[2rem] bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300"
        >
          <i class="fi fi-rs-hand-holding-seeding text-3xl"></i>
        </div>
        <h3 class="font-black text-slate-700 text-base mb-2">
          No Section Selected
        </h3>
        <p class="text-xs font-bold text-slate-400 leading-relaxed">
          Click on any section in the<br />mobile canvas to configure it.
        </p>

        <!-- Quick-add shortcuts -->
        <div class="mt-8 w-full space-y-2">
          <p
            class="text-[9px] font-black uppercase tracking-widest text-slate-300 mb-3"
          >
            Quick Add Section
          </p>
          @for (st of sectionTypes; track st.type) {
          <button
            (click)="addSection(st.type)"
            [disabled]="!activeTab"
            class="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-primary/30 hover:bg-primary/5 transition-all text-left group disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <div
              class="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-primary group-hover:text-white flex items-center justify-center text-slate-400 transition-colors shrink-0"
            >
              <i class="fi {{ st.icon }} text-sm"></i>
            </div>
            <div>
              <p
                class="text-xs font-black text-slate-700 group-hover:text-primary transition-colors"
              >
                {{ st.label }}
              </p>
              <p class="text-[9px] text-slate-400">{{ st.description }}</p>
            </div>
            <i
              class="fi fi-rs-angle-small-right text-slate-300 group-hover:text-primary ml-auto transition-colors"
            ></i>
          </button>
          }
        </div>
      </div>
      }
    </aside>
  </main>

  <!-- ═══ FULL-PAGE LOADER ═════════════════════════════════════════════════ -->
  @if (isLoading) {
  <div
    class="fixed inset-0 z-[100] bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4"
  >
    <span
      class="loading loading-infinity loading-lg text-primary scale-150"
    ></span>
    <p
      class="text-[10px] font-black uppercase tracking-[0.3em] text-primary animate-pulse"
    >
      Syncing Layout Studio...
    </p>
  </div>
  }

  <!-- ═══ TOAST NOTIFICATIONS ══════════════════════════════════════════════ -->
  <div class="fixed bottom-6 right-6 z-[200] flex flex-col-reverse gap-2 w-80">
    @for (t of toasts; track t.id) {
    <div
      class="flex items-center gap-3 px-4 py-3.5 rounded-2xl shadow-xl border text-sm font-bold"
      [ngClass]="{
        'bg-emerald-50  border-emerald-200 text-emerald-800':
          t.type === 'success',
        'bg-red-50      border-red-200     text-red-800': t.type === 'error',
        'bg-blue-50     border-blue-200    text-blue-800': t.type === 'info',
        'bg-amber-50    border-amber-200   text-amber-800': t.type === 'warning'
      }"
    >
      <i
        class="fi shrink-0 text-base"
        [ngClass]="{
          'fi-rs-check-circle text-emerald-500': t.type === 'success',
          'fi-rs-circle-xmark text-red-500': t.type === 'error',
          'fi-rs-info         text-blue-500': t.type === 'info',
          'fi-rs-triangle-warning text-amber-500': t.type === 'warning'
        }"
      ></i>
      <span class="flex-1 leading-tight">{{ t.message }}</span>
      <button
        (click)="dismissToast(t.id)"
        class="opacity-40 hover:opacity-100 transition-opacity shrink-0"
      >
        <i class="fi fi-rs-cross-small text-lg"></i>
      </button>
    </div>
    }
  </div>

  <!-- ═══ TAB MODAL ════════════════════════════════════════════════════════ -->
  <app-tab-modal
    [isTabModalOpen]="isTabModalOpen"
    [isEditingTab]="isEditingTab"
    [themes]="themes"
    [newTabForm]="newTabForm"
    (modalClosed)="closeTabModal()"
    (tabSaved)="saveTab()"
  >
  </app-tab-modal>
</div>
